<script lang="ts">
  import { onMount } from 'svelte';
  import type {
    CreateDeviceGroupRequest,
    DeviceGroupSummary,
    TenantSummary,
  } from '@ucc/shared';
  import { ApiError, createGroup, fetchGroups, fetchTenants } from '$lib/api';
  import { groupHref } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Card from '$lib/components/Card.svelte';

  let groups = $state<DeviceGroupSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let showCreate = $state(false);
  let creating = $state(false);
  let createName = $state('');
  let createDescription = $state('');
  let createTenantId = $state('');
  let actionError = $state<string | null>(null);
  let actionOk = $state<string | null>(null);

  let q = $state('');
  let tenantFilter = $state('all');

  const isAdmin = $derived(session.user?.role === 'admin');
  const isClient = $derived(session.user?.role === 'client');
  const role = $derived(session.user?.role ?? '');
  const isStaff = $derived(isAdmin || role === 'user');
  const canCreate = $derived(isStaff || isClient);
  const showTenantCol = $derived(isStaff);

  const tenantName = $derived.by(() => {
    const map = new Map(tenants.map((t) => [t.id, t.name]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  });

  const filtered = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return groups.filter((g) => {
      if (tenantFilter !== 'all' && g.tenantId !== tenantFilter) return false;
      if (!needle) return true;
      const hay = [g.name, g.description ?? '', tenantName(g.tenantId)].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  });

  const totalDevices = $derived(groups.reduce((n, g) => n + (g.deviceCount ?? 0), 0));

  async function load() {
    loading = true;
    error = null;
    try {
      const gRes = await fetchGroups();
      groups = gRes.groups;
      if (!isClient) {
        const tRes = await fetchTenants().catch(() => ({ tenants: [] as TenantSummary[] }));
        tenants = tRes.tenants;
      } else {
        tenants = [];
      }
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load groups';
      groups = [];
    } finally {
      loading = false;
    }
  }


  function resetCreateForm() {
    createName = '';
    createDescription = '';
    createTenantId = isStaff ? createTenantId : '';
    actionError = null;
  }

  async function submitCreate() {
    actionError = null;
    actionOk = null;
    const name = createName.trim();
    if (!name) {
      actionError = 'Name is required';
      return;
    }
    if (isStaff && !createTenantId.trim()) {
      actionError = 'Tenant is required';
      return;
    }
    creating = true;
    try {
      const body: CreateDeviceGroupRequest = {
        name,
        description: createDescription.trim() || null,
      };
      if (isStaff) body.tenantId = createTenantId.trim();
      const res = await createGroup(body);
      actionOk = `Created group "${res.group.name}"`;
      showCreate = false;
      createName = '';
      createDescription = '';
      await load();
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to create group';
    } finally {
      creating = false;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <header class="head">
    <div>
      <p class="eyebrow">{isClient ? 'Your fleet' : 'Fleet'}</p>
      <h2>Groups</h2>
      <p class="sub">
        {#if isClient}
          Groups for your claimed devices. Open a group to add or remove members.
        {:else}
          Organize devices for OTA and operations. Open a group to assign members.
        {/if}
      </p>
    </div>
    <div class="head-actions">
      {#if canCreate}
        <button
          type="button"
          class="refresh"
          onclick={() => {
            showCreate = !showCreate;
            actionError = null;
            actionOk = null;
          }}
        >
          {showCreate ? 'Cancel' : 'New group'}
        </button>
      {/if}
    <button type="button" class="refresh" onclick={() => void load()} disabled={loading}>
      {loading ? 'Loading…' : 'Refresh'}
    </button>
    </div>
  </header>

  {#if actionError}
    <p class="flash error" role="alert">{actionError}</p>
  {/if}
  {#if actionOk}
    <p class="flash ok" role="status">{actionOk}</p>
  {/if}

  {#if canCreate && showCreate}
    <section class="create card">
      <h3>New group</h3>
      <form
        class="create-form"
        onsubmit={(e) => {
          e.preventDefault();
          void submitCreate();
        }}
      >
        <label class="field">
          <span>Name</span>
          <input type="text" bind:value={createName} maxlength="120" required disabled={creating} />
        </label>
        <label class="field">
          <span>Description <em>(optional)</em></span>
          <input type="text" bind:value={createDescription} maxlength="500" disabled={creating} />
        </label>
        {#if isStaff}
          <label class="field">
            <span>Tenant</span>
            <select bind:value={createTenantId} required disabled={creating}>
              <option value="">Select tenant…</option>
              {#each tenants as t (t.id)}
                <option value={t.id}>{t.name}</option>
              {/each}
            </select>
          </label>
        {/if}
        <div class="form-actions">
          <button type="submit" class="primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create group'}
          </button>
          <button
            type="button"
            class="refresh"
            disabled={creating}
            onclick={() => {
              showCreate = false;
              actionError = null;
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  {/if}



  <div class="kpis">
    <div class="kpi">
      <span class="kpi-label">Groups</span>
      <strong>{groups.length}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Devices in groups</span>
      <strong>{totalDevices}</strong>
    </div>
  </div>

  <Card title="Group list" subtitle={`${filtered.length} shown`} padded={false}>
    <div class="toolbar">
      <label class="field grow">
        <span class="sr-only">Search</span>
        <input type="search" placeholder="Search groups…" bind:value={q} />
      </label>
      {#if isStaff && tenants.length > 1}
        <label class="field">
          <span class="sr-only">Tenant</span>
          <select bind:value={tenantFilter}>
            <option value="all">All tenants</option>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
      {/if}
    </div>

    {#if error}
      <p class="banner error" role="alert">{error}</p>
    {:else if loading}
      <p class="banner muted">Loading groups…</p>
    {:else if filtered.length === 0}
      <p class="banner muted">
        {#if isClient}
          No groups in your organization yet. Staff can create groups for your tenant.
        {:else}
          No groups match your filters.
        {/if}
      </p>
    {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              {#if showTenantCol}<th>Tenant</th>{/if}
              <th>Devices</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {#each filtered as g (g.id)}
              <tr
                tabindex="0"
                onclick={() => {
                  window.location.hash = groupHref(g.id);
                }}
                onkeydown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.location.hash = groupHref(g.id);
                  }
                }}
              >
                <td>
                  <a class="name" href={groupHref(g.id)} onclick={(e) => e.stopPropagation()}>{g.name}</a>
                </td>
                {#if showTenantCol}
                  <td>{tenantName(g.tenantId)}</td>
                {/if}
                <td>{g.deviceCount ?? 0}</td>
                <td class="desc">{g.description ?? '—'}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </Card>
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .eyebrow {
    margin: 0 0 0.25rem;
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    font-weight: 600;
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 650;
  }

  .sub {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
    max-width: 36rem;
  }

  .refresh {
    border: 1px solid var(--border);
    background: var(--surface);
    color: inherit;
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
    font: inherit;
    font-size: var(--text-sm, 0.9rem);
    cursor: pointer;
  }

  .refresh:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 12rem));
    gap: 0.75rem;
  }

  .kpi {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 0.85rem 1rem;
  }

  .kpi-label {
    display: block;
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
    margin-bottom: 0.25rem;
  }

  .kpi strong {
    font-size: 1.35rem;
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  .field {
    display: block;
  }

  .field.grow {
    flex: 1 1 12rem;
    min-width: 10rem;
  }

  input,
  select {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: var(--surface);
    color: inherit;
  }

  .banner {
    margin: 0;
    padding: 1.25rem 1rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .banner.muted {
    color: var(--text-muted);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm, 0.9rem);
  }

  th,
  td {
    text-align: left;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  th {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
    background: var(--surface-2, #f9fafb);
    white-space: nowrap;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover {
    background: var(--surface-hover);
  }

  .name {
    color: inherit;
    font-weight: 600;
    text-decoration: none;
  }

  .name:hover {
    color: var(--accent, #2563eb);
    text-decoration: underline;
  }

  .desc {
    color: var(--text-muted);
    max-width: 28rem;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  .head-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }
  .create {
    padding: 1rem 1.1rem;
    margin-bottom: 1rem;
  }
  .create h3 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
  }
  .create-form {
    display: grid;
    gap: 0.75rem;
  }
  .field {
    display: grid;
    gap: 0.3rem;
  }
  .field span {
    font-size: 0.85rem;
    color: var(--text-muted);
  }
  .field em {
    font-style: normal;
    opacity: 0.8;
  }
  .field input,
  .field select {
    font: inherit;
    padding: 0.45rem 0.6rem;
    border-radius: 8px;
    border: 1px solid var(--border, #333);
    background: var(--bg, #0f0f0f);
    color: inherit;
  }
  .form-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }
  .primary {
    font: inherit;
    cursor: pointer;
    border-radius: 8px;
    border: 1px solid transparent;
    padding: 0.45rem 0.9rem;
    background: var(--accent, #3b82f6);
    color: #fff;
  }
  .primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .flash {
    margin: 0 0 0.75rem;
    padding: 0.55rem 0.75rem;
    border-radius: 8px;
    font-size: 0.9rem;
  }
  .flash.error {
    background: color-mix(in srgb, #ef4444 18%, transparent);
    border: 1px solid color-mix(in srgb, #ef4444 40%, transparent);
  }
  .flash.ok {
    background: color-mix(in srgb, #22c55e 16%, transparent);
    border: 1px solid color-mix(in srgb, #22c55e 40%, transparent);
  }
</style>
